import { useCallback, useEffect, useReducer } from 'react';
import { useDmNotes } from '../../../../../hooks/useDmNotes';
import { useLiveDmNotes } from '../../../../../hooks/useLiveDmNotes';
import { useSaveDmNotes } from '../../../../../hooks/useSaveDmNotes';
import { freshestNotes, liveNotesStatus, type LiveNotesStatus } from './liveNotesStatus';
import { initNotesEditor, isNotesDirty, isNotesReadOnly, notesEditorReducer } from './notesEditor';

const UNEXPECTED_SAVE_ERROR = 'An unexpected error occurred.';

export interface DmNotesEditor {
    text: string;
    isLoading: boolean;
    isDirty: boolean;
    readOnly: boolean;
    liveStatus: LiveNotesStatus;
    saving: boolean;
    saveError: string | null;
    edit: (text: string) => void;
    discard: () => void;
    save: (onSaved: () => void) => void;
    clearSaveError: () => void;
}

function useServerNotes(patientId: string) {
    const synced = useDmNotes(patientId);
    const live = useLiveDmNotes(patientId);
    const snapshot = {
        isFetching: live.isFetching,
        isError: live.isError,
        errorStatus: live.error?.status ?? null,
        notes: live.data?.notes ?? null
    };
    const syncedNotes = synced.data ? (synced.data.notes ?? '') : null;
    return {
        notes: freshestNotes(syncedNotes, snapshot),
        status: liveNotesStatus(snapshot),
        isLoading: synced.isLoading
    };
}

export function useDmNotesEditor(patientId: string): DmNotesEditor {
    const server = useServerNotes(patientId);
    const [state, dispatch] = useReducer(notesEditorReducer, null, () =>
        initNotesEditor(patientId, server.notes)
    );

    useEffect(() => {
        dispatch({ type: 'server', patientId, notes: server.notes });
    }, [patientId, server.notes]);

    const mutation = useSaveDmNotes();
    const { mutate, reset } = mutation;
    const save = useCallback(
        (onSaved: () => void) => {
            reset();
            mutate(
                { patientId, text: state.text },
                {
                    onSuccess: (saved) => {
                        dispatch({ type: 'saved', ...saved });
                        onSaved();
                    }
                }
            );
        },
        [mutate, reset, patientId, state.text]
    );

    const edit = useCallback((text: string) => dispatch({ type: 'edit', text }), []);
    const discard = useCallback(() => dispatch({ type: 'discard' }), []);
    const isDirty = isNotesDirty(state);
    const saving = mutation.isPending;
    return {
        text: state.text,
        isLoading: server.isLoading,
        isDirty,
        readOnly: isNotesReadOnly({
            checking: server.status === 'checking',
            dirty: isDirty,
            saving
        }),
        liveStatus: server.status,
        saving,
        saveError: mutation.isError ? mutation.error.message || UNEXPECTED_SAVE_ERROR : null,
        edit,
        discard,
        save,
        clearSaveError: reset
    };
}
